import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ContentService } from '../../../shared/services/content/content.service';
import { Content } from '../../../shared/models/content.model';

@Component({
  selector: 'app-contents-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    CardModule,
    TableModule,
    ButtonModule,
    TagModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './contents-page.component.html',
  styleUrl: './contents-page.component.scss',
})
export class ContentsPageComponent implements OnInit {
  service = inject(ContentService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #router = inject(Router);

  ngOnInit(): void {
    this.service.getAll();
  }

  navigateToCreate() {
    this.#router.navigate(['/content/announcements/new']);
  }

  navigateToDetail(item: Content) {
    this.#router.navigate(['/content/announcements', item.id]);
  }

  /**
   * Mirrors the API's selection window (R29): a missing start means "ab
   * sofort", a missing end "bis auf Weiteres".
   */
  statusOf(item: Content): 'Active' | 'Expired' | 'Scheduled' {
    const now = Date.now();
    const from = item.valid_from ? Date.parse(item.valid_from) : null;
    const until = item.valid_until ? Date.parse(item.valid_until) : null;

    if (from !== null && from > now) return 'Scheduled';
    if (until !== null && until < now) return 'Expired';
    return 'Active';
  }

  statusSeverity(item: Content): 'success' | 'warn' | 'secondary' {
    const status = this.statusOf(item);
    if (status === 'Active') return 'success';
    return status === 'Scheduled' ? 'warn' : 'secondary';
  }

  onRowDelete(event: Event, item: Content) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant(
        'content.announcements.deleteConfirmHeader',
      ),
      message: this.#translate.instant(
        'content.announcements.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => this.#performDelete(item.id),
    });
  }

  #performDelete(id: number) {
    this.service.delete(id).subscribe({
      next: () => {
        this.service.getAll();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant('content.announcements.deleted'),
        });
      },
      error: () => {
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant('content.announcements.deleteError'),
        });
      },
    });
  }
}
